/**
 * Stripe billing — setup session, subscription checkout, config from API (secrets resolved server-side).
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

export const STRIPE_SETUP_PENDING_KEY = 'stepflowPendingStripeSetup';

export function isStripeCheckoutEnabled() {
  return import.meta.env.VITE_STRIPE_CHECKOUT_ENABLED === 'true';
}

/**
 * Master switch for ALL Stripe/billing UI surfaces (pricing page, usage
 * rings, upgrade cards, subscription/budget banners, "Manage subscription").
 *
 * Default ON — a normal SaaS multi-account deploy is byte-identical to before
 * (the flag is unset). Set ``VITE_BILLING_UI_ENABLED=false`` for a single-
 * account / Cognito-only deploy with no Stripe: every billing element vanishes
 * and ``/pricing`` redirects home. The backend billing endpoints already
 * degrade gracefully (usage reads work; checkout/config/portal return 503),
 * so this only suppresses the client-side chrome.
 *
 * Distinct from ``isStripeCheckoutEnabled`` (which only gates the *checkout*
 * step during signup/login) and ``VITE_BILLING_REQUIRED`` (the paid-access
 * gate). This one hides the surfaces entirely.
 */
export function isBillingUiEnabled() {
  return import.meta.env.VITE_BILLING_UI_ENABLED !== 'false';
}

export async function fetchStripeBillingStatus() {
  const token = getRestApiBearerToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const r = await fetch(`${API_BASE_URL}/auth/billing/status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(data.message || data.error || `HTTP ${r.status}`);
  }
  return {
    hasStripeCustomer: !!data.hasStripeCustomer,
    stripeCustomerId: data.stripeCustomerId || null,
    stripeSubscriptionId: data.stripeSubscriptionId || null,
    stripeSubscriptionStatus: data.stripeSubscriptionStatus || null,
    planKey: data.planKey || null,
  };
}

/**
 * @returns {Promise<{ publishableKey: string, plans: Array<Record<string, unknown>> }>}
 */
export async function fetchBillingConfig() {
  const token = getRestApiBearerToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const r = await fetch(`${API_BASE_URL}/auth/billing/config`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(data.message || data.error || `HTTP ${r.status}`);
  }
  return {
    publishableKey: data.publishableKey || '',
    plans: Array.isArray(data.plans) ? data.plans : [],
  };
}

/**
 * @param {string} planId - starter | payg | corporate
 * @param {{ successPath?: string, cancelPath?: string }} paths
 * @returns {Promise<string>} Stripe Checkout URL
 */
export async function createSubscriptionCheckout(planId, paths = {}) {
  const token = getRestApiBearerToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const r = await fetch(`${API_BASE_URL}/auth/billing/checkout-subscription`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      planId,
      successPath: paths.successPath ?? '/pricing',
      cancelPath: paths.cancelPath ?? '/pricing',
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(data.message || data.error || `HTTP ${r.status}`);
  }
  if (!data.url) {
    throw new Error('No checkout URL returned');
  }
  return data.url;
}

/**
 * @returns {Promise<{
 *   orgId: string | null,
 *   hasEntitlement: boolean,
 *   planKey: string | null,
 *   usageMonth: string,
 *   pointsUsed: number,
 *   pointsBudget: number | null,
 *   unlimited: boolean,
 *   percentUsed: number | null,
 *   warningLevel: 'info' | 'high' | 'critical' | null,
 *   periodEndUnix: number | null,
 *   periodResetUnix: number | null,  // calendar-month points reset (1st of next month UTC)
 *
 *   // Subscription-lifecycle fields (PR 6f). All null/false when the
 *   // org's subscription is in steady state.
 *   //   * cancelAtPeriodEnd        - true when the user clicked Cancel
 *   //                                 in Portal but the period hasn't ended
 *   //   * pendingPlanKey           - target plan for an end-of-period downgrade
 *   //   * pendingPlanEffectiveAtUnix - when the pending change applies
 *   //   * billingState             - "past_due" after invoice.payment_failed
 *   //                                 fires; clears on next successful payment
 *   cancelAtPeriodEnd: boolean,
 *   pendingPlanKey: string | null,
 *   pendingPlanEffectiveAtUnix: number | null,
 *   billingState: 'active' | 'past_due' | null,
 *
 *   // Deprecated legacy aliases — same numbers as ``pointsUsed`` /
 *   // ``pointsBudget``. Kept until callers migrate; remove once the
 *   // rolling-deploy window for the M4 read-side cutover is closed.
 *   usedTokens: number,
 *   includedTokenBudget: number | null,
 * }>}
 */
export async function fetchOrgBillingEntitlement() {
  const token = getRestApiBearerToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const r = await fetch(`${API_BASE_URL}/auth/billing/entitlement`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(data.message || data.error || `HTTP ${r.status}`);
  }
  // Resolve points-native fields with a fallback chain: prefer the new
  // ``pointsUsed`` / ``pointsBudget`` (post-M4); if the API hasn't been
  // redeployed yet (mid-rolling-deploy), fall back to the legacy
  // ``usedTokens`` / ``includedTokenBudget`` aliases that hold the same
  // int. Both shapes always render correctly downstream.
  const pointsUsed = typeof data.pointsUsed === 'number'
    ? data.pointsUsed
    : (typeof data.usedTokens === 'number' ? data.usedTokens : 0);
  const pointsBudget = data.pointsBudget != null
    ? data.pointsBudget
    : (data.includedTokenBudget != null ? data.includedTokenBudget : null);
  return {
    orgId: data.orgId ?? null,
    hasEntitlement: !!data.hasEntitlement,
    planKey: data.planKey || null,
    usageMonth: data.usageMonth || '',
    pointsUsed,
    pointsBudget,
    unlimited: !!data.unlimited,
    percentUsed: typeof data.percentUsed === 'number' ? data.percentUsed : null,
    warningLevel: data.warningLevel || null,
    periodEndUnix: data.periodEndUnix != null ? data.periodEndUnix : null,
    // Calendar-month counter reset (1st of next month UTC) — what the usage
    // ring's "Renews" label shows, so it matches the points number beside it
    // instead of the Stripe billing anchor. Falls back to periodEndUnix if the
    // API is pre-deploy (rolling-deploy window).
    periodResetUnix: data.periodResetUnix != null ? data.periodResetUnix : null,
    // Lifecycle fields (PR 6f). Defaulted on the client so a pre-PR-6f
    // server (rolling-deploy window) renders the banner cleanly as
    // "no lifecycle change" — same idea as the points/tokens fallback.
    cancelAtPeriodEnd: !!data.cancelAtPeriodEnd,
    pendingPlanKey: data.pendingPlanKey || null,
    pendingPlanEffectiveAtUnix:
      typeof data.pendingPlanEffectiveAtUnix === 'number'
        ? data.pendingPlanEffectiveAtUnix
        : null,
    billingState: data.billingState || null,
    // Legacy aliases preserved on the client too — components that
    // haven't been updated yet keep rendering against the old field
    // names for one rolling-deploy window.
    usedTokens: pointsUsed,
    includedTokenBudget: pointsBudget,
  };
}

/**
 * Per-month points history for the requesting org.
 *
 * Powers the in-context UpgradeCard's "Last N months: 18k / 22k / 31k pts"
 * preview. Falls back to ``[]`` (the bootstrap path) on any error so a
 * transient failure or RBAC denial doesn't block the upgrade button —
 * the card just renders its plan-spec copy instead.
 *
 * @param {{ months?: number }} [options] - window size, default 3,
 *   capped server-side at 12.
 * @returns {Promise<{
 *   orgId: string | null,
 *   months: Array<{ ym: string, points: number, isCurrentMonth: boolean }>,
 * }>}
 */
export async function fetchOrgUsageHistory(options = {}) {
  const token = getRestApiBearerToken();
  if (!token) {
    return { orgId: null, months: [] };
  }
  const months = options.months ?? 3;
  const r = await fetch(
    `${API_BASE_URL}/auth/billing/usage-history?months=${encodeURIComponent(months)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!r.ok) {
    // Don't throw — the upgrade card is best-effort. A 5xx from this
    // endpoint must NOT poison the upgrade-from-banner conversion path.
    return { orgId: null, months: [] };
  }
  const data = await r.json().catch(() => ({}));
  const monthsArr = Array.isArray(data?.months)
    ? data.months
        .map((m) => ({
          ym: typeof m?.ym === 'string' ? m.ym : '',
          points: typeof m?.points === 'number' ? m.points : 0,
          isCurrentMonth: !!m?.isCurrentMonth,
        }))
        .filter((m) => m.ym)
    : [];
  return {
    orgId: data?.orgId ?? null,
    months: monthsArr,
  };
}

/**
 * Create a Stripe Billing Portal session and return the one-time URL.
 *
 * The Portal owns self-serve subscription management (plan switch,
 * downgrade, cancel, payment-method update, invoice history). This
 * helper just hands back the URL — the caller decides whether to
 * navigate or open in a new tab. The convention in the workbench is
 * a new tab (preserves the workbench context) followed by a
 * visibility-change refetch of ``/auth/billing/entitlement`` when the
 * user returns.
 *
 * @param {{ returnPath?: string }} [options] - Path Stripe returns the
 *   user to. Defaults to ``/billing`` (matches the Lambda default).
 * @returns {Promise<string>} Portal session URL — open in a new tab.
 */
export async function openBillingPortal(options = {}) {
  const token = getRestApiBearerToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const r = await fetch(`${API_BASE_URL}/auth/billing/portal`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      returnPath: options.returnPath ?? '/billing',
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    // Surface specific server-side codes the caller can dispatch on:
    //   NoStripeCustomer — org never bought a subscription; UI should
    //                       hide "Manage subscription" entirely.
    //   Forbidden        — non-admin clicked something they shouldn't see.
    const err = new Error(data.message || data.error || `HTTP ${r.status}`);
    err.code = data.error || `HTTP_${r.status}`;
    throw err;
  }
  if (!data.url) {
    throw new Error('No portal URL returned');
  }
  return data.url;
}

export async function createStripeSetupSession(options = {}) {
  const successPath = options.successPath ?? '/network/new';
  const cancelPath = options.cancelPath ?? '/login';
  const token = getRestApiBearerToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const r = await fetch(`${API_BASE_URL}/auth/billing/setup-session`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      successPath,
      cancelPath,
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(data.message || data.error || `HTTP ${r.status}`);
  }
  if (!data.url) {
    throw new Error('No checkout URL returned');
  }
  return data.url;
}
